import { useState, useCallback } from 'react';
import SuperJSON from 'superjson';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpBatchLink, httpLink, httpSubscriptionLink, splitLink } from '@trpc/client';
import { trpcAdmin } from './lib/trpcAdmin';
import { ToastContainer } from '@duke-hq/app-admin-web/components/ToastContainer';
import { AdminRoutes } from './AdminRoutes';
import { ReactFlowProvider } from 'reactflow';
import { HelmetProvider } from 'react-helmet-async';
import { chakraTheme } from '@duke-hq/ui/theme/chakraTheme';
import { ChakraProvider, useToast } from '@chakra-ui/react';
import { AuthProviderAdmin, useAuthAdmin } from './AuthProviderAdmin';
import { SettingsProvider } from '@duke-hq/app-admin-web/contexts/SettingsContext';
import { TenantContextProvider } from './TenantContext';
import { WithGoogleOAuth } from './WithGoogleOAuth';
import { WithFlags } from './WithFlags';
import { WithNotifications } from './WithNotifications';
import { WithReactQueryDevtools } from './WithReactQueryDevtools';
import { createVersionCheckingLink } from '@duke-hq/ui/version/createVersionedTrpcClient';
import { unauthorisedRedirectLink } from '@duke-hq/libauth-client/unauthorisedRedirectLink';
import { t } from '@lingui/core/macro';
import { NavButtonProvider } from '@duke-hq/ui/components/MainSidebar/NavButtonContext';
import { SubSidebarProvider } from '@duke-hq/ui/components/SubSidebar/SubSidebarContext';
import { FilesTrpcClientProvider } from '@duke-hq/svc-files-ui/FilesTrpcClientProvider';
import { getTenantContext } from '@duke-hq/libctx';
import { AdminTestClientProvider } from './lib/adminTestClient';

const url = '/api/admin';

function AppAdminWithFilesAuth() {
  const { user } = useAuthAdmin();
  const createToken = trpcAdmin.auth.createFilesServiceToken.useMutation();
  const filesServiceUrl = getTenantContext()?.apis.FILES_API_ENDPOINT;

  const getAuthToken = useCallback(async () => {
    return await createToken.mutateAsync();
  }, [createToken]);

  return (
    <FilesTrpcClientProvider
      getAuthToken={getAuthToken}
      enableCdnSession={user != null}
      filesServiceUrl={filesServiceUrl}
    >
      <WithFlags>
        <WithNotifications>
          <WithReactQueryDevtools>
            <SettingsProvider>
              <NavButtonProvider initialExpanded={false}>
                <SubSidebarProvider>
                  <AdminTestClientProvider>
                    <AdminRoutes />
                  </AdminTestClientProvider>
                </SubSidebarProvider>
              </NavButtonProvider>
            </SettingsProvider>
          </WithReactQueryDevtools>
        </WithNotifications>
      </WithFlags>
    </FilesTrpcClientProvider>
  );
}

export function AppAdmin() {
  const toast = useToast();

  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpcAdmin.createClient({
      links: [
        createVersionCheckingLink({
          clientVersion: process.env.APP_BUILD_HASH || '',
          onVersionMismatch: () => {
            toast({
              description: t`There is a new version of the app, please reload the page.`,
              status: 'info',
              variant: 'left-accent',
              isClosable: true,
              duration: null,
            });
          },
        }),
        unauthorisedRedirectLink({
          loginPath: '/login',
          publicPaths: ['/signout'],
        }),
        splitLink({
          condition(op) {
            // Route subscriptions to SSE link
            return op.type === 'subscription';
          },
          // when condition is true, use SSE for subscriptions
          true: httpSubscriptionLink({
            url,
            transformer: SuperJSON,
            eventSourceOptions: () => ({
              withCredentials: true,
            }),
          }),
          // when condition is false, use regular HTTP links
          false: splitLink({
            condition(op) {
              // Prevent any operation on the auth.* route from being sent
              // in a batch.
              if (op.path.startsWith('auth.')) {
                return true;
              }

              // check for context property `skipBatch`
              return Boolean(op.context.skipBatch);
            },
            // when condition is true, use normal request
            true: httpLink({
              url,
              transformer: SuperJSON,
            }),
            // when condition is false, use batching
            false: httpBatchLink({
              url,
              transformer: SuperJSON,
            }),
          }),
        }),
      ],
    }),
  );

  return (
    <TenantContextProvider>
      <WithGoogleOAuth>
        <trpcAdmin.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <HelmetProvider>
              <ReactFlowProvider>
                <ChakraProvider theme={chakraTheme}>
                  <AuthProviderAdmin>
                    <AppAdminWithFilesAuth />
                  </AuthProviderAdmin>
                  <ToastContainer />
                </ChakraProvider>
              </ReactFlowProvider>
            </HelmetProvider>
          </QueryClientProvider>
        </trpcAdmin.Provider>
      </WithGoogleOAuth>
    </TenantContextProvider>
  );
}
